import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Download, Loader2, Film, FileText, Captions, CaptionsOff, Heart, ShieldCheck, MapPin, Link2, Check, BookOpen, ArrowLeft, ArrowRight } from "lucide-react";
import ShareButtons from "@/ShareButtons";
import LanguagePicker from "@/LanguagePicker";
import StoryBodyView from "@/StoryBodyView";
import StoryReactions from "@/StoryReactions";
import DiscoverRail from "@/DiscoverRail";
import StoryLabelBadges from "@/StoryLabelBadges";
import PodcastSubscribeRow from "@/PodcastSubscribeRow";
import SocialFollowRow from "@/SocialFollowRow";
import { useTranslation } from "@/i18n";
import { mediaUrl } from "@/mediaUrl";
import { usePublicConfig } from "@/usePublicConfig";
import { entrySource } from "@/entrySource";
import { resolveDonationUrl, getStoryStatus } from "@/donateUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const LANGUAGE_FLAG = {
  en: "🇬🇧", de: "🇩🇪", fr: "🇫🇷", es: "🇪🇸", it: "🇮🇹", el: "🇬🇷",
  tr: "🇹🇷", ar: "🇸🇦", nl: "🇳🇱", ru: "🇷🇺",
};

/** Public per-story page. Pass `slug="..."` to fetch by slug, or omit
 *  to fetch the latest *featured* story (used by the bare `/` URL). */
export default function StoryViewer({ slug }) {
  const [t, locale, setLocale] = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [job, setJob] = useState(null);
  // ── New independent dual-picker model ──────────────────────────────
  // `audioLang` controls which language's AUDIO plays. Defaults to the
  // story's source language (the speaker's original voice — the most
  // authentic experience, especially for rescue/charity stories).
  // `subLang` controls which language's SUBTITLES overlay (or "off").
  // Defaults to the per-story `default_subtitle_lang` override if set,
  // else the viewer's UI locale. This decouples "what I hear" from
  // "what I read" — a German story with English subs is now a one-
  // dropdown choice, not a hidden voice-mode toggle.
  const [audioLang, setAudioLang] = useState(null);
  const [subLang, setSubLang] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [reqLang, setReqLang] = useState("");
  const [reqSent, setReqSent] = useState(false);
  const [ccOn, setCcOn] = useState(true);
  const [shortCopied, setShortCopied] = useState(false);
  const [copiedFlash, setCopiedFlash] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // `prepareError` surfaces a fatal-but-recoverable problem with the
  // currently-requested AUDIO language (e.g. the story's source upload
  // was lost and the dub can never be generated). Without this, the
  // player just spins indefinitely with no signal to the viewer. Keyed
  // by lang code so switching away clears the message automatically.
  // Values: "source_missing" | "timeout" | null
  const [prepareError, setPrepareError] = useState(null);
  const [prepareErrorLang, setPrepareErrorLang] = useState(null);
  const videoRef = useRef(null);
  const pollRef = useRef(null);
  const prepareTimeoutRef = useRef(null);
  const publicCfg = usePublicConfig();
  const donate = resolveDonationUrl(job, publicCfg);
  const storyStatus = getStoryStatus(job);

  const CATEGORY_LABEL = {
    charity: t("cat_charity"),
    animal_rescue: t("cat_animal_rescue"),
    social_impact: t("cat_social_impact"),
    environment: t("cat_environment"),
  };

  // Parse `?audio=X&sub=Y` from the URL on first load so shared links
  // land viewers on the exact audio/subtitle combo the sharer picked.
  // Invalid codes are ignored — we'll fall back to the smart defaults
  // computed in loadJob().
  const SUPPORTED_CODES = new Set(["en","de","fr","es","it","el","tr","ar","nl","ru","zh","hi","th","lt"]);
  const readUrlState = () => {
    if (typeof window === "undefined") return { audio: null, sub: null };
    const params = new URLSearchParams(window.location.search);
    const a = (params.get("audio") || "").toLowerCase();
    const s = (params.get("sub") || "").toLowerCase();
    return {
      audio: SUPPORTED_CODES.has(a) ? a : null,
      sub: (s === "off" || SUPPORTED_CODES.has(s)) ? s : null,
    };
  };

  const loadJob = async (attempt = 0) => {
    try {
      const langRes = await axios.get(`${API}/languages`);
      setLanguages(langRes.data);
      const featRes = slug
        ? await axios.get(`${API}/stories/${slug}`)
        : await axios.get(`${API}/featured`);
      setJob(featRes.data);
      const urlState = readUrlState();
      // Default audio = URL ?audio= if valid, else the speaker's
      // ORIGINAL voice (source language). Per user direction Q1=a.
      setAudioLang((prev) => prev || urlState.audio || featRes.data.language);
      // Default subs: (1) URL ?sub= if valid, (2) per-story override,
      // (3) viewer's UI locale, (4) "off" if locale == source.
      const sourceLang = featRes.data.language;
      const override = featRes.data.default_subtitle_lang;
      const localeSub = (locale && locale !== sourceLang) ? locale : "off";
      setSubLang((prev) => prev || urlState.sub || override || localeSub);
    } catch (_e) {
      // Transient hiccup (server busy, brief 5xx): retry silently before
      // showing an error — fixes "story doesn't open until I refresh".
      const status = _e?.response?.status;
      if (attempt < 3 && (!status || status >= 500)) {
        setTimeout(() => loadJob(attempt + 1), 1500 * (attempt + 1));
        return;
      }
      setError(slug
        ? "This story doesn't exist or hasn't been published yet."
        : "No stories have been published yet. Please check back soon.");
    }
  };

  useEffect(() => { loadJob(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);

  // Fire a one-shot view-ping per page load so the homepage "Most watched"
  // rail can rank by real interest. Backend rate-limits per-IP+slug to
  // 30 minutes, so a refresh-spammer can't inflate the number.
  useEffect(() => {
    if (!job?.slug) return;
    const sessionKey = `viewed:${job.slug}`;
    if (sessionStorage.getItem(sessionKey)) return;
    sessionStorage.setItem(sessionKey, "1");
    axios.post(`${API}/stories/${job.slug}/view`, { ref: entrySource() || document.referrer || "" }).catch(() => { /* tolerated */ });
  }, [job?.slug]);

  // Keep <title> + Open Graph meta tags in sync with the loaded story so
  // social shares (Facebook, WhatsApp, iMessage) show useful previews.
  useEffect(() => {
    if (!job) return;
    const fullTitle = `${job.title} · Storylo`;
    document.title = fullTitle;
    const desc = job.impact_summary
      || `Watch "${job.title}" voiced in 14 languages on Storylo.`;
    const img = job.thumbnail_url
      ? mediaUrl(job.thumbnail_url)
      : "https://customer-assets.emergentagent.com/job_narration-hub-16/artifacts/storylo-og.png";
    const url = typeof window !== "undefined"
      ? `${window.location.origin}/s/${job.slug}`
      : "";
    const metaPairs = [
      ["property", "og:title", fullTitle],
      ["property", "og:description", desc],
      ["property", "og:image", img],
      ["property", "og:url", url],
      ["property", "og:type", "video.other"],
      ["name", "twitter:title", fullTitle],
      ["name", "twitter:description", desc],
      ["name", "twitter:image", img],
      ["name", "description", desc],
    ];
    for (const [attr, key, value] of metaPairs) {
      let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", value);
    }
  }, [job]);


  // Mirror audio/sub state into the URL so deep-links + shared URLs
  // (`/s/atilas-progress?audio=de&sub=en`) drop visitors onto the exact
  // combo the sharer picked. Uses replaceState so back/forward isn't
  // littered with picker tweaks. Source-language audio + viewer-locale
  // subs (the global defaults) are omitted from the URL to keep canonical
  // URLs short and stable when nothing has been customised.
  useEffect(() => {
    if (!job || typeof window === "undefined") return;
    const source = job.language;
    const defaultSub = job.default_subtitle_lang
      || (locale && locale !== source ? locale : "off");
    const params = new URLSearchParams(window.location.search);
    if (audioLang && audioLang !== source) params.set("audio", audioLang);
    else params.delete("audio");
    if (subLang && subLang !== defaultSub) params.set("sub", subLang);
    else params.delete("sub");
    const qs = params.toString();
    const next = `${window.location.pathname}${qs ? "?" + qs : ""}${window.location.hash}`;
    if (next !== window.location.pathname + window.location.search + window.location.hash) {
      window.history.replaceState(null, "", next);
    }
  }, [audioLang, subLang, job, locale]);

  // Auto-trigger the subbed-variant build the moment a viewer needs it
  // but it doesn't exist yet. Covers both the deep-link case (visitor
  // arrives via /s/<slug>?audio=de&sub=en) AND the dropdown case (the
  // onSubChange handler also fires it, but only when the user actually
  // changes the dropdown — landing visitors would otherwise wait
  // forever). De-duped per (story, lang) via a ref so we don't spam
  // the endpoint on every poll-driven re-render.
  const triggeredBuildRef = useRef(new Set());
  useEffect(() => {
    if (!job) return;
    if (!subLang || subLang === "off" || subLang === job.language) return;
    if (audioLang !== job.language) return;  // only when listening to source audio
    const entry = job.languages?.[subLang];
    if (entry?.subbed_video_url) return;
    // Three cases that need different endpoints:
    //  • entry doesn't exist OR status pending/failed → fire `prepare`
    //    so the dub + script get built first. Then on the next poll
    //    the second branch below kicks build-subbed.
    //  • entry status=done but no subbed_video_url → fire build-subbed.
    //  • entry status=processing → already in flight, just wait.
    const key = `${job.id}:${subLang}`;
    if (!entry || ["pending", "failed", "processing"].includes(entry.status)) {
      // "processing" is included on purpose: a pod restart can leave a
      // dub stuck in processing forever. The prepare endpoint is
      // idempotent — it no-ops for genuinely live renders and re-kicks
      // stale ones.
      const prepKey = `${key}:prepare`;
      if (triggeredBuildRef.current.has(prepKey)) return;
      triggeredBuildRef.current.add(prepKey);
      axios.post(`${API}/videos/${job.id}/languages/${subLang}/prepare`).catch(() => {});
      return;
    }
    // entry.status === "done" path
    const subKey = `${key}:subbed`;
    if (triggeredBuildRef.current.has(subKey)) return;
    triggeredBuildRef.current.add(subKey);
    axios.post(`${API}/videos/${job.id}/languages/${subLang}/build-subbed`).catch(() => {});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [job?.id, subLang, audioLang, job?.languages]);

  // Auto-trigger the "Original voice + tail clips" backfill when the
  // viewer is on source audio + subs off, but the variant doesn't
  // exist yet on the story (legacy Re-dub renders predate this field).
  // Without this, those legacy stories silently dropped the tail clip
  // when picking "Original voice". One trigger per story.
  const triggeredOrigTailsRef = useRef(new Set());
  useEffect(() => {
    if (!job) return;
    if (audioLang !== job.language) return;
    if (job.original_with_tails_url) return;
    if (!job.tail_count) return; // no tails to crossfade — nothing to build
    if (triggeredOrigTailsRef.current.has(job.id)) return;
    triggeredOrigTailsRef.current.add(job.id);
    axios.post(`${API}/videos/${job.id}/build-original-with-tails`).catch(() => {});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [job?.id, audioLang, job?.original_with_tails_url, job?.tail_count]);

  // Auto-trigger an audio-language dub render when a viewer arrives via
  // a deep link like `/s/<slug>?audio=zh` and that dub hasn't been
  // built yet. Without this, the URL-param path never fired `prepare`
  // (only the dropdown handler did) so the spinner would spin until
  // the 4-min timeout below kicked in. One trigger per (story, lang)
  // via a ref. Surfaces 404/410 immediately as `prepareError`.
  const triggeredAudioPrepRef = useRef(new Set());
  useEffect(() => {
    if (!job) return;
    if (!audioLang || audioLang === job.language) return;
    const entry = job.languages?.[audioLang];
    if (entry?.status === "done" && entry?.video_url) return;
    // NOTE: we deliberately do NOT skip status "processing" — a pod
    // restart can strand a dub in processing forever. prepare is
    // idempotent and re-kicks only genuinely stale renders.
    const key = `${job.id}:${audioLang}`;
    if (triggeredAudioPrepRef.current.has(key)) return;
    triggeredAudioPrepRef.current.add(key);
    (async () => {
      try {
        await axios.post(`${API}/videos/${job.id}/languages/${audioLang}/prepare`);
        try {
          const r = await axios.get(`${API}/videos/${job.id}`);
          setJob(r.data);
        } catch (_e) { /* polling will retry */ }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 410) {
          setPrepareError("source_missing");
          setPrepareErrorLang(audioLang);
        }
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [job?.id, audioLang, job?.languages]);


  // Keep refreshing while a dub render OR a subbed-variant build OR
  // the original-with-tails backfill is in flight. Polls every 4 s.
  useEffect(() => {
    if (!job) return;
    const preparingDub = Object.values(job.languages || {}).some(
      (l) => l?.status === "processing" || l?.status === "pending",
    );
    const waitingForActiveSub = (
      subLang && subLang !== "off" && subLang !== job.language
      && !job.languages?.[subLang]?.subbed_video_url
      && job.languages?.[subLang]?.status !== "failed"
    );
    const waitingForOrigTails = (
      audioLang === job.language && job.tail_count > 0 && !job.original_with_tails_url
    );
    const needsPolling = preparingDub || waitingForActiveSub || waitingForOrigTails;
    if (!needsPolling) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/videos/${job.id}`);
        setJob(r.data);
      } catch (_e) { /* noop */ }
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [job, subLang, audioLang]);

  // Hard timeout for the prepare/preparing spinner: after 4 minutes
  // with the requested audio language still not ready, give up and
  // show a "taking longer than expected" message so the viewer isn't
  // stuck staring at an indefinite loader. The polling effect above
  // continues in the background — if the dub eventually finishes the
  // banner will clear on the next poll-driven re-render.
  useEffect(() => {
    if (!job) return;
    if (audioLang === job.language) return; // source audio is always instant
    const entry = job.languages?.[audioLang];
    const ready = entry?.status === "done" && entry?.video_url;
    if (ready) {
      if (prepareTimeoutRef.current) {
        clearTimeout(prepareTimeoutRef.current);
        prepareTimeoutRef.current = null;
      }
      // Clear any stale timeout error now that the dub IS ready.
      if (prepareError === "timeout" && prepareErrorLang === audioLang) {
        setPrepareError(null);
        setPrepareErrorLang(null);
      }
      return;
    }
    if (prepareError === "source_missing" && prepareErrorLang === audioLang) {
      return; // fatal error already shown, no timer needed
    }
    if (prepareTimeoutRef.current) return; // timer already running
    prepareTimeoutRef.current = setTimeout(() => {
      setPrepareError("timeout");
      setPrepareErrorLang(audioLang);
      prepareTimeoutRef.current = null;
    }, 4 * 60 * 1000);
    return () => {
      if (prepareTimeoutRef.current) {
        clearTimeout(prepareTimeoutRef.current);
        prepareTimeoutRef.current = null;
      }
    };
  }, [job, audioLang, prepareError, prepareErrorLang]);


  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <Film className="w-10 h-10 mx-auto text-[color:var(--muted)] opacity-50" />
          <p className="font-display text-xl mt-4">{t("not_found")}</p>
          <a href="/" className="text-xs text-[color:var(--muted)] mt-4 inline-block hover:text-white">{t("back_home")}</a>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[color:var(--accent)]" />
      </div>
    );
  }

  const sourceLang = job.language;
  // Per-story language allow-list: when set, viewers only see these
  // languages in the pickers (source always included).
  const allowedSet = job.allowed_langs?.length
    ? new Set([...job.allowed_langs, sourceLang])
    : null;
  const offeredLanguages = allowedSet
    ? languages.filter((l) => allowedSet.has(l.code))
    : languages;
  const missingLanguages = allowedSet
    ? languages.filter((l) => !allowedSet.has(l.code))
    : [];
  const isAudioSource = audioLang === sourceLang;
  // The "active" language is whichever side determines which transcript
  // text & metadata strings (article, transcript panel, etc.) should
  // render in. We prefer the subtitle language because that's what the
  // reader is consuming visually; fall back to audio for "Subs Off".
  const activeLang = (subLang && subLang !== "off") ? subLang : audioLang;
  const langEntry = job.languages?.[audioLang];
  const subLangEntry = (subLang && subLang !== "off") ? job.languages?.[subLang] : null;

  // Video URL resolution:
  //   • Source audio + subs: play the SUBBED variant of subLang (= source
  //     video with subs burned in). Falls back to the raw original upload
  //     if the subbed variant hasn't been built yet.
  //   • Source audio + no subs: play the raw original upload (the
  //     creator's actual MP4 with the real speaker's voice).
  //   • Dub audio + any subs: play the dub video_url. VTT track in subLang
  //     overlays on top (if subLang ≠ "off" and the VTT exists).
  //
  // CRITICAL: when audio is the source language, we prefer
  // `original_with_tails_url` (real speaker's voice + tail clips
  // crossfaded). Falls back to `original_source_url` (raw upload, no
  // tail), and finally to `video_url` (Publish-as-is = source + tails;
  // Re-dub = AI dub of same language — wrong voice).
  const sourceMediaUrl = job.original_with_tails_url || job.original_source_url || job.video_url;
  let playUrl = null;
  let isReady = false;
  let isPreparing = false;
  if (isAudioSource) {
    if (subLang && subLang !== "off") {
      playUrl = subLangEntry?.subbed_video_url || sourceMediaUrl;
      isReady = !!playUrl;
      isPreparing = subLangEntry?.status === "processing" || subLangEntry?.status === "pending";
    } else {
      playUrl = sourceMediaUrl;
      isReady = !!playUrl;
    }
  } else {
    playUrl = langEntry?.video_url;
    isReady = langEntry?.status === "done" && !!playUrl;
    isPreparing = langEntry?.status === "processing" || langEntry?.status === "pending";
  }
  const videoUrl = isReady ? mediaUrl(playUrl) : null;
  // VTT overlays only when audio is a DUB (not source — source audio uses
  // burned-in subs from the subbed variant) AND subLang is set.
  const vttUrl = (
    isReady && !isAudioSource && subLang && subLang !== "off"
      && langEntry?.status === "done"
  )
    ? mediaUrl(`/api/media/subtitles/${job.id}_${subLang}.vtt`)
    : null;
  const downloadName = `${job.slug || "story"}-${audioLang}.mp4`;
  const audioLangName = languages.find((l) => l.code === audioLang)?.name || audioLang;
  const subLangName = (subLang && subLang !== "off")
    ? (languages.find((l) => l.code === subLang)?.name || subLang)
    : "Off";
  // Show a "Building…" banner whenever the viewer asked for subs but
  // the subbed variant isn't ready yet. Covers THREE distinct waiting
  // states so the viewer never silently stares at raw source video:
  //  (a) Sub-language not built yet (status pending/processing/null)
  //      → translation + TTS + render still running on backend.
  //  (b) Sub-lang dub is done but subbed_video_url not yet (auto-fired)
  //      → just waiting for the burn-in ffmpeg pass.
  //  (c) Dub audio mode + dub still building → covered by isPreparing.
  const subbedWaiting = (
    isAudioSource && subLang && subLang !== "off" && subLang !== sourceLang
    && !subLangEntry?.subbed_video_url
    && subLangEntry?.status !== "failed"
  );
  // Split the waiting message based on which stage we're at so the
  // estimate is honest: "translating ~90s" vs "burning subs ~60s".
  const subbedWaitingStage = (() => {
    if (!subLangEntry || subLangEntry.status === "pending") return "queued";
    if (subLangEntry.status === "processing") return "translating";
    if (subLangEntry.status === "done" && !subLangEntry.subbed_video_url) return "burning";
    return null;
  })();
  const subbedFailed = (
    isAudioSource && subLang && subLang !== "off" && subLang !== sourceLang
    && subLangEntry?.status === "failed"
  );

  // When the viewer picks a new audio language, kick the on-demand dub
  // render if it isn't built yet. Also sync the global UI locale so the
  // surrounding chrome (transcript panel, donate buttons, article body)
  // matches what they're listening to.
  const onAudioChange = async (code) => {
    setAudioLang(code);
    // Clear any stale prepare-error from a previous language pick. If
    // THIS new language also fails, the catch below will re-set it.
    setPrepareError(null);
    setPrepareErrorLang(null);
    if (code !== sourceLang) {
      try { setLocale(code); } catch (_e) { /* tolerated */ }
    }
    const info = job.languages?.[code];
    if (code !== sourceLang && (!info || info.status === "pending" || info.status === "failed")) {
      setBusy(true);
      try {
        await axios.post(`${API}/videos/${job.id}/languages/${code}/prepare`);
        const r = await axios.get(`${API}/videos/${job.id}`);
        setJob(r.data);
      } catch (err) {
        // Surface a permanent failure rather than letting the spinner
        // run forever. 400 / 404 / 410 → the backend can never generate
        // this dub (source gone or config rejected). Any other status →
        // treat as transient and let the polling loop retry.
        const status = err?.response?.status;
        if (status === 400 || status === 404 || status === 410) {
          setPrepareError("source_missing");
          setPrepareErrorLang(code);
        }
      }
      setBusy(false);
    }
  };

  // When subs change, build the subbed variant on demand if audio is
  // source. Dubbed audio uses on-the-fly VTT which is always available
  // once the dub finishes rendering, so no build is needed there.
  const onSubChange = async (code) => {
    setSubLang(code);
    if (code === "off" || code === sourceLang) return;
    const info = job.languages?.[code];
    // If the subbed variant doesn't exist yet AND we're listening to
    // source audio, kick a build. Dubbed listeners get VTT overlay
    // without needing a fresh render.
    if (isAudioSource && info?.status === "done" && !info?.subbed_video_url) {
      setBusy(true);
      try {
        await axios.post(`${API}/videos/${job.id}/languages/${code}/build-subbed`);
        const r = await axios.get(`${API}/videos/${job.id}`);
        setJob(r.data);
      } catch (_e) { /* handled */ }
      setBusy(false);
    } else if (!info || info.status === "pending" || info.status === "failed") {
      // The lang isn't even built yet; kick the full prepare which will
      // also yield the VTT we need.
      setBusy(true);
      try {
        await axios.post(`${API}/videos/${job.id}/languages/${code}/prepare`);
        const r = await axios.get(`${API}/videos/${job.id}`);
        setJob(r.data);
      } catch (_e) { /* handled */ }
      setBusy(false);
    }
  };

  // Build the share URL with the viewer's current Audio/Subtitle choice
  // baked in via query params — so when a supporter shares Atila with
  // German voice + English subs, their friend lands on that exact combo
  // (not a viewer-locale-defaulted fallback). Source audio + default
  // subs are omitted to keep the URL short.
  const buildShareSuffix = () => {
    if (!job) return "";
    const p = new URLSearchParams();
    const defaultSub = job.default_subtitle_lang
      || (locale && locale !== job.language ? locale : "off");
    if (audioLang && audioLang !== job.language) p.set("audio", audioLang);
    if (subLang && subLang !== defaultSub) p.set("sub", subLang);
    const qs = p.toString();
    return qs ? `?${qs}` : "";
  };
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/s/${job.slug}${buildShareSuffix()}`
    : "";
  // Facebook / Twitter / LinkedIn crawlers don't execute JS and never see
  // the SPA's per-story OG tags. We point their share dialog at the
  // backend's `/api/share/{slug}` endpoint, which server-renders the OG
  // HTML (title, description, auto-generated 1200×630 thumbnail) and
  // sets `og:url` back to /s/{slug} — so the post the user actually
  // publishes still links to the human-friendly URL. WhatsApp / iMessage
  // / Email keep using the canonical URL since they crawl it themselves.
  const crawlerUrl = typeof window !== "undefined"
    ? `${window.location.origin}/api/share/${job.slug}${buildShareSuffix()}`
    : "";
  // Copy actions hand out the crawler-readable URL too: pasting a plain
  // /s/ link into Facebook shows NO thumbnail (FB never executes the SPA's
  // JS), while /api/share/{slug} serves per-story OG tags and instantly
  // meta-refreshes humans to the pretty /s/ page. Fresh per-click token
  // busts Facebook's 30-day preview cache.
  const socialCopyUrl = () => {
    if (!crawlerUrl) return shareUrl;
    const bust = `v=${Date.now().toString(36)}`;
    return crawlerUrl.includes("?") ? `${crawlerUrl}&${bust}` : `${crawlerUrl}?${bust}`;
  };

  return (
    <div className="min-h-screen" data-testid="story-viewer">
      <header className="px-6 md:px-10 py-6 md:py-8 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="home-link">
            <img src="/brand-s.png" alt="Storylo" className="w-9 h-9 rounded-full" data-testid="brand-logo" />
            <div>
              <div className="font-display text-lg leading-none">Storylo</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)] mt-1">
                {t("tagline")}
              </div>
            </div>
          </a>
          <nav className="flex items-center gap-3 sm:gap-4 text-sm">
            <a href="/" className="text-[color:var(--muted)] hover:text-white transition-colors hidden sm:inline">{t("nav_all")}</a>
            <a href="/impact" className="text-[color:var(--muted)] hover:text-white transition-colors hidden sm:inline">{t("nav_impact")}</a>
            <LanguagePicker />
          </nav>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10 md:py-14">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {job.main_category_label && (
              <a
                href={`/?main_category=${job.main_category}`}
                className="text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border border-[color:var(--accent)]/40 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/10 transition-colors"
                data-testid="story-main-category"
              >
                {job.main_category_label}
              </a>
            )}
            {job.category && job.category !== "general" && (
              <span className="text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border border-white/15 text-[color:var(--muted)]" data-testid="story-category">
                {CATEGORY_LABEL[job.category] || job.category}
              </span>
            )}
            {job.verified && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full bg-emerald-500/85 text-black font-medium" data-testid="story-verified">
                <ShieldCheck className="w-3 h-3" /> {t("badge_verified")}
              </span>
            )}
            {job.location && (
              <span className="inline-flex items-center gap-1 text-[11px] text-[color:var(--muted)]" data-testid="story-location">
                <MapPin className="w-3 h-3" /> {job.location}
              </span>
            )}
            {job.country && !job.location && (
              <span className="inline-flex items-center gap-1 text-[11px] text-[color:var(--muted)]" data-testid="story-country">
                <MapPin className="w-3 h-3" /> {job.country}{job.year != null && ` · ${job.year}`}
              </span>
            )}
            {(job.labels && job.labels.length > 0) && (
              <StoryLabelBadges labels={job.labels} size="sm" />
            )}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="story-title">
            {job.title}
          </h1>
          {job.impact_summary && (
            <p className="text-[color:var(--muted)] max-w-2xl mt-5 leading-relaxed text-base sm:text-lg" data-testid="story-impact-summary">
              {job.impact_summary}
            </p>
          )}

          {job.series && (
            <div
              className="mt-6 panel px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 max-w-2xl"
              data-testid="story-series-banner"
            >
              <a
                href={`/book/${encodeURIComponent(job.series.key)}`}
                className="inline-flex items-center gap-2 text-sm text-[color:var(--accent)] hover:underline"
                data-testid="story-series-link"
              >
                <BookOpen className="w-4 h-4" />
                Part {job.series.part} of {job.series.total} · {job.series.title}
              </a>
              <span className="flex-1" />
              {job.series.prev && (
                <a
                  href={`/s/${encodeURIComponent(job.series.prev.slug)}`}
                  className="inline-flex items-center gap-1.5 text-xs text-[color:var(--muted)] hover:text-white transition-colors"
                  title={job.series.prev.title}
                  data-testid="story-series-prev"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Previous
                </a>
              )}
              {job.series.next && (
                <a
                  href={`/s/${encodeURIComponent(job.series.next.slug)}`}
                  className="inline-flex items-center gap-1.5 text-xs text-white hover:text-[color:var(--accent)] transition-colors"
                  title={job.series.next.title}
                  data-testid="story-series-next"
                >
                  Next episode <ArrowRight className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          )}

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-10 mt-10 lg:mt-14">
            {/* Player */}
            <section className="lg:col-span-3 space-y-5">
              <div className="panel overflow-hidden">
                <div className="relative bg-black aspect-video flex items-center justify-center">
                  {isReady ? (
                    <video
                      key={`${job.id}-${audioLang}-${subLang}`}
                      ref={videoRef}
                      src={videoUrl}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-contain"
                      data-testid="public-video"
                      onLoadedMetadata={() => {
                        // Force the native track into the requested mode the moment it's parsed.
                        const v = videoRef.current;
                        if (!v) return;
                        for (let i = 0; i < v.textTracks.length; i++) {
                          v.textTracks[i].mode = ccOn ? "showing" : "hidden";
                        }
                      }}
                    >
                      {vttUrl && (
                        <track
                          kind="subtitles"
                          src={vttUrl}
                          srcLang={subLang}
                          label={subLangName}
                          default
                        />
                      )}
                    </video>
                  ) : (prepareError && prepareErrorLang === audioLang) ? (
                    <div className="text-center px-6 py-12 max-w-md mx-auto" data-testid="dub-error-state">
                      <Film className="w-10 h-10 mx-auto text-[color:var(--muted)] opacity-60" />
                      <div className="font-display text-lg mt-4">
                        {prepareError === "source_missing"
                          ? t("dub_unavailable_title")
                          : t("dub_timeout_title")}
                      </div>
                      <div className="text-xs text-[color:var(--muted)] mt-2 leading-relaxed">
                        {prepareError === "source_missing"
                          ? t("dub_unavailable_hint")
                          : t("dub_timeout_hint")}
                      </div>
                      <button
                        type="button"
                        className="btn-secondary text-xs mt-5 inline-flex items-center gap-2"
                        onClick={() => onAudioChange(sourceLang)}
                        data-testid="dub-error-back-to-source"
                      >
                        {LANGUAGE_FLAG[sourceLang] || "🌐"} {languages.find((l) => l.code === sourceLang)?.name || sourceLang}
                      </button>
                    </div>
                  ) : (
                    <div className="text-center px-6 py-12">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-[color:var(--accent)]" />
                      <div className="font-display text-lg mt-4">
                        {isPreparing ? t("preparing") : t("loading")}
                      </div>
                      <div className="text-xs text-[color:var(--muted)] mt-2">
                        {t("preparing_hint")}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Subbed-variant build status banner. Shows whenever the
                  viewer picked source audio + a sub language but the
                  burned-in subbed variant hasn't been built yet — so
                  the player is currently serving the raw source (no
                  visible subs). The polling effect above auto-refreshes
                  the page when subbed_video_url appears, at which point
                  this banner unmounts and the player swaps to the
                  subbed variant. */}
              {subbedWaiting && (
                <div
                  className="p-4 rounded-lg border border-[color:var(--accent)]/40 bg-[color:var(--accent)]/5 flex items-start gap-3"
                  data-testid="subbed-building-banner"
                  role="status"
                  aria-live="polite"
                >
                  <Loader2 className="w-5 h-5 animate-spin text-[color:var(--accent)] flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[color:var(--accent)]">
                      {subbedWaitingStage === "queued"   && `Preparing ${subLangName} subtitles…`}
                      {subbedWaitingStage === "translating" && `Translating to ${subLangName} (~90 seconds)`}
                      {subbedWaitingStage === "burning"  && `Burning ${subLangName} subtitles into the ${audioLangName} original (~60 seconds)`}
                    </div>
                    <div className="text-[11px] text-[color:var(--muted)] mt-1 leading-relaxed">
                      You're hearing the original {audioLangName} narration. The page auto-refreshes the moment the {subLangName} subtitles are ready. (Or pick a different audio language above to listen instead.)
                    </div>
                  </div>
                </div>
              )}
              {subbedFailed && (
                <div
                  className="p-4 rounded-lg border border-red-500/40 bg-red-500/5 flex items-start gap-3"
                  data-testid="subbed-failed-banner"
                  role="alert"
                >
                  <CaptionsOff className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-red-300">
                      {subLangName} subtitles couldn't be built
                    </div>
                    <div className="text-[11px] text-[color:var(--muted)] mt-1 leading-relaxed">
                      Try picking a different language above, or check back in a few minutes.
                    </div>
                  </div>
                </div>
              )}

              {/* Copy-link nudge. Lives right under the player so the
                  "I want to send this to a friend" impulse maps to a
                  single tap. The pill explicitly promises the friend
                  will land on the SAME audio+subs combo — defeating
                  the assumption that "the link forgets what I picked."*/}
              {isReady && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const toCopy = socialCopyUrl();
                      if (navigator.clipboard && window.isSecureContext) {
                        await navigator.clipboard.writeText(toCopy);
                      } else {
                        const ta = document.createElement("textarea");
                        ta.value = toCopy;
                        ta.style.position = "fixed";
                        ta.style.left = "-9999px";
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand("copy");
                        document.body.removeChild(ta);
                      }
                      setCopiedFlash(true);
                      setTimeout(() => setCopiedFlash(false), 2400);
                    } catch (_e) { /* tolerated */ }
                  }}
                  className={`w-full text-left p-3 rounded-lg border transition-all flex items-center gap-3 ${
                    copiedFlash
                      ? "border-emerald-500/60 bg-emerald-500/10"
                      : "border-[color:var(--accent)]/40 bg-[color:var(--accent)]/5 hover:bg-[color:var(--accent)]/10"
                  }`}
                  data-testid="copy-share-link"
                  aria-label="Copy share link"
                >
                  {copiedFlash ? (
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  ) : (
                    <Link2 className="w-4 h-4 text-[color:var(--accent)] flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${copiedFlash ? "text-emerald-300" : "text-[color:var(--accent)]"}`}>
                      {copiedFlash ? "Copied! Friends will hear it just like you do." : "Copy link to this exact experience"}
                    </div>
                    <div className="text-[11px] text-[color:var(--muted)] mt-0.5 truncate">
                      {copiedFlash
                        ? "Audio + subtitles bake into the link."
                        : `${audioLangName}${subLang && subLang !== "off" ? ` audio · ${subLangName} subs` : " audio · subs off"}`}
                    </div>
                  </div>
                </button>
              )}


              {isReady && (
                <div className="flex flex-wrap items-center gap-3">
                  <a href={videoUrl} download={downloadName} className="btn-primary inline-flex items-center gap-2" data-testid="public-download">
                    <Download className="w-4 h-4" />
                    {t("download_in", { lang: audioLangName })}
                  </a>
                  {donate && (
                    <a href={donate.url} target="_blank" rel="noopener noreferrer"
                       className="btn-primary inline-flex items-center gap-2"
                       style={{ background: "#10b981", color: "#04150d" }}
                       data-testid="public-donate">
                      <Heart className="w-4 h-4" />
                      {t("support_btn", { org: job.organization_name || t("support_default") })}
                    </a>
                  )}
                  {storyStatus === "goal_reached" && (
                    <span
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border"
                      style={{ background: "rgba(16, 185, 129, 0.1)", borderColor: "rgba(16, 185, 129, 0.35)", color: "#34d399" }}
                      data-testid="public-goal-reached"
                    >
                      <Heart className="w-4 h-4 fill-current" />
                      Goal reached — thank you
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const next = !ccOn;
                      setCcOn(next);
                      const v = videoRef.current;
                      if (v) {
                        for (let i = 0; i < v.textTracks.length; i++) {
                          v.textTracks[i].mode = next ? "showing" : "hidden";
                        }
                      }
                    }}
                    className={`btn-ghost inline-flex items-center gap-2 ${ccOn ? "text-[color:var(--accent)] border-[color:var(--accent)]" : ""}`}
                    data-testid="public-cc-toggle"
                    aria-pressed={ccOn}
                  >
                    {ccOn ? <Captions className="w-4 h-4" /> : <CaptionsOff className="w-4 h-4" />}
                    {ccOn ? t("captions_on") : t("captions_off")}
                  </button>
                  <ShareButtons
                    url={shareUrl}
                    crawlerUrl={crawlerUrl}
                    title={job.title}
                    text={`Watch "${job.title}" in 10 languages`}
                  />
                </div>
              )}

              {/* Podcast subscribe CTA — converts the warmest possible
                  lead (someone who finished a story) into a permanent
                  subscriber across every future story we publish. */}
              <PodcastSubscribeRow />

              {/* Social follow CTA — Instagram / Facebook / YouTube. */}
              <div className="mt-4">
                <SocialFollowRow />
              </div>

              {/* Short promotional URL — the memorable link used in
                  promos, QR codes and social posts. */}
              {job.short_slug && (
                <div
                  className="mt-4 flex flex-wrap items-center gap-3 text-sm"
                  data-testid="story-short-link"
                >
                  <span className="text-[color:var(--muted)] text-xs uppercase tracking-[0.18em]">
                    Share this story
                  </span>
                  <button
                    type="button"
                    onClick={async () => {
                      const short = `${window.location.origin}/${job.short_slug}`;
                      try {
                        await navigator.clipboard.writeText(short);
                        setShortCopied(true);
                        setTimeout(() => setShortCopied(false), 1800);
                      } catch (_e) { /* clipboard unavailable */ }
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--accent)]/40 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/10 transition-colors font-medium"
                    data-testid="story-short-link-copy"
                    title="Copy the short link"
                  >
                    {shortCopied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                    {shortCopied ? "Copied!" : `${window.location.host}/${job.short_slug}`}
                  </button>
                </div>
              )}
            </section>

            {/* Language picker + transcript */}
            <aside className="lg:col-span-2 space-y-6">
              <div className="panel p-6 space-y-5">
                {/* AUDIO picker — what they HEAR. Defaults to source so
                    the speaker's original voice plays first. */}
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] block mb-3">
                    Audio
                  </label>
                  <select
                    value={audioLang}
                    onChange={(e) => onAudioChange(e.target.value)}
                    disabled={busy}
                    className="input-base w-full px-4 py-3 text-base"
                    data-testid="public-audio-select"
                  >
                    {offeredLanguages.map((l) => {
                      const info = job.languages?.[l.code];
                      const isSource = l.code === sourceLang;
                      const state = isSource ? " · Original voice"
                                 : info?.status === "processing" ? " · " + t("preparing").replace("…","")
                                 : info?.status === "pending" ? " · " + t("preparing").replace("…","")
                                 : info?.status === "failed" ? " · —"
                                 : info?.status === "done" ? " · Dubbed"
                                 : "";
                      return (
                        <option key={l.code} value={l.code}>
                          {LANGUAGE_FLAG[l.code] || "🏳️"}  {l.name}{state}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-[11px] text-[color:var(--muted)] mt-2 leading-relaxed">
                    Default plays the speaker's real voice. Switch to a dub to hear it in a different language — same gender as the original speaker.
                  </p>
                  {missingLanguages.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5" data-testid="request-language-block">
                      {reqSent ? (
                        <p className="text-[11px] text-[color:var(--accent)]" data-testid="request-language-thanks">
                          Thank you — we've passed your request to the storyteller.
                        </p>
                      ) : (
                        <div className="flex items-center gap-2">
                          <select
                            value={reqLang}
                            onChange={(e) => setReqLang(e.target.value)}
                            className="input-base flex-1 px-2 py-1.5 text-xs"
                            data-testid="request-language-select"
                          >
                            <option value="">Missing your language? Request it…</option>
                            {missingLanguages.map((l) => (
                              <option key={l.code} value={l.code}>{l.name}</option>
                            ))}
                          </select>
                          <button
                            disabled={!reqLang}
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/stories/${job.slug}/request-language`, { lang: reqLang });
                                setReqSent(true);
                              } catch (_e) { setReqSent(true); }
                            }}
                            className="text-xs px-3 py-1.5 rounded border border-white/15 disabled:opacity-40 hover:border-[color:var(--accent)]"
                            data-testid="request-language-send"
                          >
                            Send
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* SUBTITLES picker — what they READ. Independent from
                    audio so a German story can have English subs even
                    while playing in the original German voice. */}
                <div className="pt-5 border-t border-white/5">
                  <label className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] block mb-3">
                    Subtitles
                  </label>
                  <select
                    value={subLang || "off"}
                    onChange={(e) => onSubChange(e.target.value)}
                    disabled={busy}
                    className="input-base w-full px-4 py-3 text-base"
                    data-testid="public-sub-select"
                  >
                    <option value="off">— Off —</option>
                    {offeredLanguages.map((l) => {
                      if (l.code === sourceLang && isAudioSource) {
                        // Source-language subs over source-language audio = karaoke. Hide.
                        return null;
                      }
                      const info = job.languages?.[l.code];
                      const state = info?.status === "processing" ? " · " + t("preparing").replace("…","")
                                 : info?.status === "pending" ? " · " + t("preparing").replace("…","")
                                 : info?.status === "failed" ? " · —"
                                 : "";
                      return (
                        <option key={l.code} value={l.code}>
                          {LANGUAGE_FLAG[l.code] || "🏳️"}  {l.name}{state}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-[11px] text-[color:var(--muted)] mt-2 leading-relaxed">
                    Read along in any of 10 languages, independent of the audio you've picked.
                  </p>
                </div>
              </div>

              {(job.languages?.[activeLang]?.translated_text || langEntry?.translated_text) && (
                <div className="panel p-6">
                  <button
                    onClick={() => setShowTranscript((s) => !s)}
                    className="flex items-center gap-2 text-sm text-[color:var(--muted)] hover:text-white transition-colors"
                    data-testid="public-transcript-toggle"
                  >
                    <FileText className="w-4 h-4" />
                    {showTranscript ? t("hide_transcript") : t("show_transcript")}
                  </button>
                  {showTranscript && (
                    <div className="mt-4 text-sm leading-relaxed whitespace-pre-wrap max-h-[40vh] overflow-auto pr-2"
                         data-testid="public-transcript">
                      {job.languages?.[activeLang]?.translated_text || langEntry?.translated_text}
                    </div>
                  )}
                </div>
              )}
            </aside>
          </div>

          {/* Long-form article body — only renders if the creator has
              written one. Auto-translates into the active playback
              language so foreign-audio readers see foreign text. */}
          <StoryBodyView slug={job.slug} lang={activeLang} story={job} />

          {/* Lightweight thumbs-up / thumbs-down — quietly aggregates a
              "was this meaningful" signal so we can later promote the
              most-loved stories alongside the most-watched. */}
          <StoryReactions
            slug={job.slug}
            initialHelpful={job.helpful_count || 0}
            initialNotHelpful={job.not_helpful_count || 0}
          />
        </div>
      </main>

      {/* Discover rail — AI-curated 3 thematically-paired stories. Renders
          itself null when there's nothing else published, so brand-new
          libraries don't show an empty placeholder. */}
      <DiscoverRail slug={job.slug} />

      <section className="px-6 md:px-10 mt-12 md:mt-16" data-testid="story-agent-opus-credit">
        <div className="max-w-[1200px] mx-auto">
          <div className="panel p-6 md:p-7 border-l-[3px] border-l-sky-400/70">
            <div className="text-[10px] tracking-[0.22em] uppercase text-sky-300 mb-2">
              Made with Agent Opus AI
            </div>
            <p className="text-sm leading-relaxed text-[color:var(--muted)] max-w-3xl">
              Every Storylo documentary is proudly created using Agent Opus AI,
              helping us transform carefully researched stories into engaging
              visual experiences.
            </p>
            <p className="text-sm leading-relaxed text-[color:var(--muted)] max-w-3xl mt-3">
              If you&apos;ve enjoyed this documentary and would like to create your
              own history, inspirational or educational videos, we&apos;d be grateful
              if you used our affiliate link below. By signing up through our
              link, you&apos;ll be supporting Storylo at no extra cost to you,
              helping us continue producing stories that inform, inspire and
              encourage understanding.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <a
                href="https://tolt.link/storylo"
                target="_blank"
                rel="sponsored noopener noreferrer"
                className="btn-primary text-sm inline-flex items-center gap-2 px-5 py-2.5"
                data-testid="agent-opus-affiliate-link"
              >
                Start creating with Agent Opus today <span aria-hidden="true">→</span>
              </a>
              <span className="text-xs text-[color:var(--muted)]">
                Thank you for supporting Storylo and helping us keep great stories flowing.
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-10 mt-12 md:mt-16" data-testid="story-about-storylo">
        <div className="max-w-[1200px] mx-auto">
          <div className="panel p-6 md:p-7 border-l-[3px] border-l-[color:var(--accent)] flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--accent)] mb-2">
                Storylo
              </div>
              <p className="text-sm leading-relaxed text-[color:var(--muted)] max-w-2xl">
                {t("story_about_line")}
              </p>
            </div>
            <a href="/" className="btn-ghost text-sm whitespace-nowrap inline-flex items-center gap-1.5"
               data-testid="story-learn-more">
              {t("learn_more")} <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </section>

      <footer className="mt-10 border-t border-white/5 px-6 md:px-10 py-6">
        <div className="max-w-[1200px] mx-auto flex flex-wrap items-center justify-between gap-3 text-xs text-[color:var(--muted)]">
          <div>
            {t("footer_made_by")} <a href="https://hadfun.co.uk" className="text-white hover:text-[color:var(--accent)] transition-colors">Hadfun</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
